import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'outline' | 'ghost' | 'gradient';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  rounded = 'lg',
  className = '',
  disabled,
  ...props
}, ref) => {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed relative";
  
  const variants = {
    primary: "bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 focus:ring-primary-500 shadow-soft hover:shadow-medium border border-primary-700/20",
    secondary: "bg-white text-gray-900 hover:bg-gray-50 focus:ring-gray-500 shadow-soft hover:shadow-medium border border-gray-200/50",
    danger: "bg-gradient-to-r from-error-600 to-error-700 text-white hover:from-error-700 hover:to-error-800 focus:ring-error-500 shadow-soft hover:shadow-medium border border-error-700/20",
    success: "bg-gradient-to-r from-success-600 to-success-700 text-white hover:from-success-700 hover:to-success-800 focus:ring-success-500 shadow-soft hover:shadow-medium border border-success-700/20",
    warning: "bg-gradient-to-r from-warning-500 to-warning-600 text-white hover:from-warning-600 hover:to-warning-700 focus:ring-warning-500 shadow-soft hover:shadow-medium border border-warning-600/20",
    outline: "border-2 border-gray-300/60 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:ring-gray-500 shadow-soft",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500 hover:shadow-soft",
    gradient: "bg-primary-gradient text-white hover:shadow-strong focus:ring-primary-500 shadow-medium border border-white/20"
  };

  const sizes = {
    xs: "px-2 py-1.5 text-xs",
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-6 py-3 text-base",
    xl: "px-8 py-4 text-lg"
  };

  const roundedClasses = {
    sm: "rounded-md",
    md: "rounded-lg", 
    lg: "rounded-xl",
    xl: "rounded-2xl",
    full: "rounded-full"
  };

  const widthClass = fullWidth ? "w-full" : "";
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${roundedClasses[rounded]} ${widthClass} ${className}`}
      disabled={isDisabled}
      {...props}
    >
      <div className="flex items-center justify-center">
        {loading ? (
          <div className="mr-2">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : leftIcon ? (
          <span className="mr-2 flex items-center">{leftIcon}</span>
        ) : null}
        
        {children}
        
        {!loading && rightIcon && (
          <span className="ml-2 flex items-center">{rightIcon}</span>
        )}
      </div>
    </button>
  );
});

Button.displayName = 'Button';

export default Button;